import React from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Paper,
  Group,
  ThemeIcon,
  List,
} from '@mantine/core';
import {
  IconCheck,
  IconTargetArrow,
  IconBrain,
  IconUsers,
  IconTrendingUp,
  IconNetwork,
} from '@tabler/icons-react';
import { appUrl } from '@/packages/shared/utils/subdomainUrls';

export function LandingPage() {
  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Paper
          shadow="xl"
          p="xl"
          radius="md"
          style={{ backgroundColor: 'var(--mantine-color-brand-0)' }}
        >
          <Stack align="center" gap="lg">
            <Title order={1} ta="center" c="brand.8">
              Unlock Your Team's Agile Potential with AgileX
            </Title>
            <Text size="xl" ta="center" c="gray.7" maw={700}>
              Understand your team's agile maturity, identify areas for growth,
              and drive continuous improvement with our comprehensive assessment
              surveys and actionable insights.
            </Text>
            <Group justify="center" mt="md">
              <Button
                component="a"
                href={appUrl('/auth/login?tab=register')}
                size="lg"
                radius="xl"
                rightSection={<IconTargetArrow size={20} />}
              >
                Get Started - It's Free
              </Button>
              <Button
                component="a"
                href={appUrl('/surveys')}
                variant="outline"
                size="lg"
                radius="xl"
              >
                Explore Surveys
              </Button>
            </Group>
          </Stack>
        </Paper>

        <Stack gap="lg">
          <Title order={2} ta="center">
            Why Agile Maturity Matters
          </Title>
          <Text ta="center" c="dimmed" maw={800} mx="auto">
            In today's fast-paced environment, high-performing agile teams are
            crucial for success. Understanding your current agile maturity helps
            you:
          </Text>
          <List
            spacing="sm"
            size="md"
            center
            icon={
              <ThemeIcon color="teal" size={24} radius="xl">
                <IconCheck size={16} />
              </ThemeIcon>
            }
            maw={600}
            mx="auto"
          >
            <List.Item>
              Pinpoint strengths and weaknesses in your agile practices.
            </List.Item>
            <List.Item>
              Foster a culture of continuous learning and improvement.
            </List.Item>
            <List.Item>
              Align team efforts with strategic business objectives.
            </List.Item>
            <List.Item>
              Enhance team collaboration, performance, and predictability.
            </List.Item>
            <List.Item>
              Deliver greater value to your customers, faster.
            </List.Item>
          </List>
        </Stack>

        <Paper shadow="md" p="xl" radius="md">
          <Title order={2} ta="center" mb="lg">
            Features to Empower Your Agile Journey
          </Title>
          <Group grow align="flex-start">
            {[
              {
                icon: IconBrain,
                title: 'Comprehensive Assessments',
                description:
                  'Utilize expertly crafted surveys covering key agile dimensions.',
              },
              {
                icon: IconTrendingUp,
                title: 'Actionable Insights',
                description:
                  'Receive clear reports and track maturity progress over time.',
              },
              {
                icon: IconUsers,
                title: 'Team-Centric Approach',
                description:
                  'Designed for Scrum Masters, Agile Coaches, and their teams.',
              },
              {
                icon: IconNetwork,
                title: 'Future Platform Growth',
                description:
                  'Evolving into a hub for coach networking, Q&A, and tailored learning resources.',
              },
            ].map(feature => (
              <Stack key={feature.title} align="center" ta="center" maw={300}>
                <ThemeIcon variant="light" size={60} radius={60} mb="sm">
                  <feature.icon size={30} />
                </ThemeIcon>
                <Text fw={700} size="lg">
                  {feature.title}
                </Text>
                <Text size="sm" c="dimmed">
                  {feature.description}
                </Text>
              </Stack>
            ))}
          </Group>
        </Paper>

        <Stack align="center" mt="xl">
          <Title order={3} ta="center">
            Ready to Elevate Your Agile Practices?
          </Title>
          <Text ta="center" c="dimmed">
            Join AgileX today and start your journey towards higher team
            performance.
          </Text>
          <Button
            component="a"
            href={appUrl('/auth/login?tab=register')}
            size="lg"
            radius="xl"
            mt="md"
          >
            Sign Up Now
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
}
